import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from "react";

import { FormattedFile, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { EntityRecord } from "@/types/entity";
import { ISwivuProject } from "@/types/SwivuProject";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { formatFileMetadata } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { MetadataRow } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { TComboOption } from "@/modern/components/ui/combobox";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { formatFile, getFileExtension } from "@/modern/utils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

import { useEntityManager } from "@/react/hooks/useEntityManager";

import { useImageLoading } from "../../../../hooks/useImageLoading";
import { useFileNavigation } from "../hooks/useFileNavigation";
import { useFileSpotIncentive } from "../../../../hooks/useFileSpotIncentive";
import { useDocumentExtraction, ExtractionRequestBody } from "../components/extraction/hooks/useDocumentExtraction";
import { useDocumentTextCache } from "../components/extraction/hooks/useDocumentTextCache";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { usePdfTextSearch } from "../components/extraction/hooks/usePdfTextSearch";
import { ExtractionType, ExtractionResults } from "../components/extraction/types";

interface FileDetailModalContextValue {
  // File state
  file: FormattedFile | null;
  files: FormattedFile[];
  imageFile: FormattedFile | null;
  imageUrlToLoad: string | undefined;

  // Image loading state
  imageLoading: boolean;
  showSpinner: boolean;
  displayedImageUrl: string | undefined;
  currentImageUrl: string | undefined;
  handleLoadComplete: () => void;

  // Navigation state
  hasPrevious: boolean;
  hasNext: boolean;
  handlePrevious: () => void;
  handleNext: () => void;

  // File permissions
  canEditFile: boolean;
  handleFileDownload: (file: FormattedFile) => void;
  deleteFile: (id: string | number) => void;

  // Spot incentive state
  shouldShowAdjustPoints: (file: FormattedFile | null) => boolean;
  handleAdjustPoints: (file: FormattedFile, isCreatingUnreleasedRecord?: boolean, onReviewClick?: () => void) => Promise<void>;

  // UI state
  activeTab: "review" | "summary";
  setActiveTab: (tab: "review" | "summary") => void;
  summaryTab: "details" | "findings" | "points" | "share";
  setSummaryTab: (tab: "details" | "findings" | "points" | "share") => void;
  isAnnotating: boolean;
  setIsAnnotating: (value: boolean) => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  isAnalyzeDialogOpen: boolean;
  setIsAnalyzeDialogOpen: (open: boolean) => void;

  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Computed values
  metadataRows: MetadataRow[];
  shouldShowSpotIncentiveButton: boolean;

  // External dependencies
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  handleFileOpen: (file: FormattedFile) => void;

  // Handlers
  handleAnnotating: () => void;
  handleCreateSpotIncentive: () => void;
  fileChangesVersion: number;

  // Extraction panel state
  isExtractable: boolean;
  isExtractionPanelOpen: boolean;
  setIsExtractionPanelOpen: (open: boolean) => void;
  activeExtraction: ExtractionType | null;
  extractionResults: ExtractionResults | null;
  extractionLoading: boolean;
  extractionError: string | null;
  cachedExtractionResults: Partial<Record<ExtractionType, ExtractionResults>>;
  handleRunExtraction: (type: ExtractionType) => Promise<void>;
  handleViewExtraction: (type: ExtractionType) => void;
  handleCloseExtractionPanel: () => void;
  handleBackToOperationList: () => void;

  // Loading phase for two-phase extraction loading
  loadingPhase: "analyzing" | "extracting" | null;

  // PDF search
  pdfSearchText: string | null;
  handleSearchInPdf: (searchText: string) => void;
  clearPdfSearch: () => void;

  // Entity link
  copyEntityLink: ReturnType<typeof useEntityManager>['copyEntityLink'];
}

const FileDetailModalContext = createContext<FileDetailModalContextValue | undefined>(undefined);

interface FileDetailModalProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  file: FormattedFile | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
}

export const FileDetailModalProvider = ({ children, entityRecord, file, open, setOpen, project, swivuAppGrants, editorHandler }: FileDetailModalProviderProps) => {
  const [localFile, setLocalFile] = useState<FormattedFile | null>(file);
  const { files, handleFileOpen, deleteFile, handleFileDownload } = useEntityFileContext();
  const [activeTab, setActiveTab] = useState<"review" | "summary">("review");
  const [summaryTab, setSummaryTab] = useState<"details" | "findings" | "points" | "share">("details");
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnalyzeDialogOpen, setIsAnalyzeDialogOpen] = useState(false);

  // Extraction panel state
  const [isExtractionPanelOpen, setIsExtractionPanelOpen] = useState(false);
  const [activeExtraction, setActiveExtraction] = useState<ExtractionType | null>(null);
  const [extractionResults, setExtractionResults] = useState<ExtractionResults | null>(null);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const [cachedExtractionResults, setCachedExtractionResults] = useState<Partial<Record<ExtractionType, ExtractionResults>>>({});
  const { runExtraction, cancelExtraction } = useDocumentExtraction();
  const { prefetchDocumentText, awaitDocumentText, cancelPrefetch } = useDocumentTextCache();
  const [loadingPhase, setLoadingPhase] = useState<"analyzing" | "extracting" | null>(null);

  // PDF text search
  const { pdfSearchText, searchInPdf, clearSearch } = usePdfTextSearch();

  const handleSearchInPdf = useCallback((searchText: string) => {
    searchInPdf(searchText);
  }, [searchInPdf]);

  // Check if file is an image to determine if we should load it
  const imageFile = useMemo(() => (isImageFileTyped(localFile) ? localFile : null), [localFile]);
  const imageUrlToLoad = imageFile?.originalImageUrl;
  // Compute canEditFile directly
  const canEditFile = Boolean(localFile?.canEdit);

  const EXTRACTABLE_EXTENSIONS = ["pdf", "xlsx", "xls", "docx", "csv", "tsv", "txt"];
  const isExtractable = EXTRACTABLE_EXTENSIONS.includes(getFileExtension(localFile?.name));

  // Custom hooks for business logic
  const { imageLoading, showSpinner, displayedImageUrl, currentImageUrl, handleLoadComplete } = useImageLoading(imageFile?.id, imageUrlToLoad);
  const { hasPrevious, hasNext, handlePrevious, handleNext } = useFileNavigation(file, files, handleFileOpen);
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const { subscribe: subscribeToFile, version: fileChangesVersion } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });
  const { user, entityManager } = useSwivuBryntumContext();
  const { copyEntityLink } = entityManager;

  useEffect(() => {
    return subscribeToFile(({record: changedRecord}) => {
      if (!localFile?.isCCImage && changedRecord.id === localFile?.id) {
        const [f] = formatFile([changedRecord], user);
        setLocalFile(f);
        return true;
      }
      return false;
    });
  }, [subscribeToFile, localFile?.id]);

  useEffect(() => {
    if (file) {
      setLocalFile(file);
    }
  }, [file?.id]);

  // Computed values
  const metadataRows = useMemo(() => formatFileMetadata(localFile), [localFile]);
  const shouldShowSpotIncentiveButton = shouldShowAdjustPoints(localFile);

  // Handlers
  const handleAnnotating = useCallback(() => {
    if (localFile && project && localFile.originalImageUrl) {
      setIsAnnotating(true);
    }
  }, [localFile, project, setOpen]);

  const handleCreateSpotIncentive = useCallback(() => {
    if (localFile) {
      handleAdjustPoints(localFile, true, (newFile?: BoardFile) => {
        if (localFile.isCCImage && newFile) {
          const [f] = formatFile([newFile], user);
          handleFileOpen(f);
        }
        setActiveTab("summary");
        setSummaryTab("points");
        setOpen(true);
      });
    }
  }, [localFile, handleAdjustPoints, setActiveTab, setOpen]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // Builds the shared extraction request body for all document-ai endpoints
  const buildExtractionBody = useCallback((fileId: number): ExtractionRequestBody => {
    const selectedBoard = getSavedBoardSelection();
    const board_id = selectedBoard?.id ? Number(selectedBoard.id) : null;
    const source = localFile?.isCCImage ? "companycam" : "swivu";
    return { file_id: fileId, source, board_id };
  }, [localFile?.isCCImage]);

  // Extraction handlers
  const handleRunExtraction = useCallback(async (type: ExtractionType) => {
    if (!localFile) return;

    setActiveExtraction(type);
    setExtractionResults(null);
    setExtractionLoading(true);
    setExtractionError(null);

    try {
      setLoadingPhase("analyzing");
      await awaitDocumentText();

      setLoadingPhase("extracting");
      const results = await runExtraction(type, buildExtractionBody(localFile.id as number));
      if (results) {
        setExtractionResults(results);
        setCachedExtractionResults((prev) => ({ ...prev, [type]: results }));
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error ??
        error?.message ??
        "Extraction failed";
      setExtractionError(typeof message === "string" ? message : String(message));
    } finally {
      setExtractionLoading(false);
      setLoadingPhase(null);
    }
  }, [localFile, runExtraction, awaitDocumentText, buildExtractionBody]);

  const handleViewExtraction = useCallback((type: ExtractionType) => {
    const cached = cachedExtractionResults[type];
    if (cached) {
      setActiveExtraction(type);
      setExtractionResults(cached);
      setExtractionLoading(false);
      setExtractionError(null);
    } else {
      handleRunExtraction(type);
    }
  }, [cachedExtractionResults, handleRunExtraction]);

  const handleCloseExtractionPanel = useCallback(() => {
    cancelExtraction();
    clearSearch();
    setIsExtractionPanelOpen(false);
    setActiveExtraction(null);
    setExtractionResults(null);
    setExtractionLoading(false);
    setExtractionError(null);
    setLoadingPhase(null);
  }, [cancelExtraction, clearSearch]);

  const handleBackToOperationList = useCallback(() => {
    cancelExtraction();
    clearSearch();
    setActiveExtraction(null);
    setExtractionResults(null);
    setExtractionLoading(false);
    setExtractionError(null);
    setLoadingPhase(null);
  }, [cancelExtraction, clearSearch]);

  // Reset extraction panel and cached results when file changes
  useEffect(() => {
    handleCloseExtractionPanel();
    cancelPrefetch();
    setCachedExtractionResults({});
  }, [localFile?.id]);

  // Prefetch document text cache when extraction panel opens for extractable files
  useEffect(() => {
    if (isExtractionPanelOpen && localFile?.id && isExtractable) {
      prefetchDocumentText(buildExtractionBody(localFile.id as number));
    }
  }, [isExtractionPanelOpen, localFile?.id, isExtractable]);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab("review");
    }
  }, [open]);

  // Add keyboard listener for navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Don't handle navigation if user is typing in an input field
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.hasAttribute("contenteditable")
        );

        if (isInputFocused) {
          return; // Let the input handle the arrow keys normally
        }

        e.preventDefault();
        if (e.key === "ArrowLeft") {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handlePrevious, handleNext]);

  const value: FileDetailModalContextValue = {
    // File state
    file: localFile,
    files,
    imageFile,
    imageUrlToLoad,

    // Image loading state
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,

    // Navigation state
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,

    // File permissions
    canEditFile,
    handleFileDownload,
    deleteFile,

    // Spot incentive state
    shouldShowAdjustPoints,
    handleAdjustPoints,

    // UI state
    activeTab,
    setActiveTab,
    summaryTab,
    setSummaryTab,
    isAnnotating,
    setIsAnnotating,
    isFullscreen,
    toggleFullscreen,
    isAnalyzeDialogOpen,
    setIsAnalyzeDialogOpen,

    // Modal state
    open,
    setOpen,

    // Computed values
    metadataRows,
    shouldShowSpotIncentiveButton,

    // External dependencies
    entityRecord,
    project,
    swivuAppGrants,
    editorHandler,
    handleFileOpen,

    // Handlers
    handleAnnotating,
    handleCreateSpotIncentive,

    fileChangesVersion,

    // Extraction panel state
    isExtractable,
    isExtractionPanelOpen,
    setIsExtractionPanelOpen,
    activeExtraction,
    extractionResults,
    extractionLoading,
    extractionError,
    cachedExtractionResults,
    handleRunExtraction,
    handleViewExtraction,
    handleCloseExtractionPanel,
    handleBackToOperationList,

    // Loading phase for two-phase extraction loading
    loadingPhase,

    // PDF search
    pdfSearchText,
    handleSearchInPdf,
    clearPdfSearch: clearSearch,

    // Entity link
    copyEntityLink,
  };

  return <FileDetailModalContext.Provider value={value}>{children}</FileDetailModalContext.Provider>;
};

export const useFileDetailModal = () => {
  const context = useContext(FileDetailModalContext);
  if (context === undefined) {
    throw new Error("useFileDetailModal must be used within a FileDetailModalProvider");
  }
  return context;
};
